import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
var _excluded = ["className", "style", "title", "inputId", "defaultValue", "value", "onChange", "onKeyDown", "onBlur", "onFocus", "disabled", "readonly", "error", "errorNode", "required", "children", "maxLength", "name", "hideTitleOnInput", "tabIndex", "inputProps", "inputRef", "autosize"];
import * as React from "react";
import { useCallback, useMemo } from 'react';
import ClassNames from "classnames";
import { useControlled, KEY_VALUES, mergeRefs, setupGetInstanceId } from '../utils';
var getInstanceId = setupGetInstanceId();
var TextArea = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var className = props.className,
    style = props.style,
    title = props.title,
    _props$inputId = props.inputId,
    inputId = _props$inputId === void 0 ? String.fromCharCode(65 + Math.floor(Math.random() * 26)) + Date.now() : _props$inputId,
    _props$defaultValue = props.defaultValue,
    defaultValue = _props$defaultValue === void 0 ? "" : _props$defaultValue,
    valueProp = props.value,
    onChange = props.onChange,
    onKeyDown = props.onKeyDown,
    onBlur = props.onBlur,
    onFocus = props.onFocus,
    disabled = props.disabled,
    readonly = props.readonly,
    error = props.error,
    errorNode = props.errorNode,
    required = props.required,
    children = props.children,
    maxLength = props.maxLength,
    name = props.name,
    _props$hideTitleOnInp = props.hideTitleOnInput,
    hideTitleOnInput = _props$hideTitleOnInp === void 0 ? false : _props$hideTitleOnInp,
    _props$tabIndex = props.tabIndex,
    tabIndexProp = _props$tabIndex === void 0 ? 0 : _props$tabIndex,
    inputProps = props.inputProps,
    inputRefProp = props.inputRef,
    _props$autosize = props.autosize,
    autosize = _props$autosize === void 0 ? false : _props$autosize,
    otherProps = _objectWithoutPropertiesLoose(props, _excluded);
  var _React$useState = React.useState(false),
    keyboardFocus = _React$useState[0],
    setKeyboardFocus = _React$useState[1];
  var _React$useState2 = React.useState(false),
    focus = _React$useState2[0],
    setFocus = _React$useState2[1];
  var _useControlled = useControlled(valueProp, defaultValue),
    value = _useControlled[0],
    setValue = _useControlled[1];
  var textAreaRef = React.useRef(null);
  var containerRef = React.useRef(null);
  var inputRef = React.useRef(null);
  React.useEffect(function () {
    if (autosize !== false && inputRef.current) {
      inputRef.current.style.height = 'auto';
      setTimeout(function () {
        if (inputRef.current) inputRef.current.style.height = inputRef.current.scrollHeight + 'px';
      }, 200);
      if (typeof autosize === 'object') {
        if (autosize.maxRows) inputRef.current.style.maxHeight = autosize.maxRows * 20 + 'px';
        if (autosize.minRows) inputRef.current.style.minHeight = autosize.minRows * 20 + 'px';
      }
    }
  }, [JSON.stringify(autosize), inputRef]);
  var handleClick = function handleClick(event) {
    if (containerRef.current && containerRef.current.contains(event.target) && focus) {
      return;
    }
    if (textAreaRef.current) {
      textAreaRef.current.focus();
    }
    setFocus(true);
    setKeyboardFocus(false);
  };
  var onkeydown = function onkeydown(event) {
    if (event.key === KEY_VALUES.TAB) {
      setFocus(true);
      setKeyboardFocus(true);
      // @ts-ignore
      if (event.target !== inputRef.current) {
        setFocus(false);
        return;
      }
    }
    onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(event);
  };
  var handleChange = function handleChange(event) {
    var value = event.target.value;
    setValue(value);
    onChange === null || onChange === void 0 ? void 0 : onChange(value, event);
    if (autosize !== false) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = inputRef.current.scrollHeight + 'px';
    }
  };
  var handleInputBlur = useCallback(function (event) {
    onBlur === null || onBlur === void 0 ? void 0 : onBlur(event);
    if (containerRef.current && containerRef.current.contains(event.target)) {
      return;
    }
    setKeyboardFocus(false);
    setFocus(false);
  }, [onBlur]);
  var handleInputFocus = useCallback(function (event) {
    onFocus === null || onFocus === void 0 ? void 0 : onFocus(event);
  }, [onFocus]);
  var fieldTitleWrapperClass = ClassNames("ap-field-title-container", {
    "required": required
  });
  var fieldWrapperClass = ClassNames("ap-field textarea", className, {
    "has-length": value !== "",
    "readonly": readonly,
    "disabled": disabled,
    "error": error
  });
  var fieldContainerClass = ClassNames("ap-field-container", {
    "hide-title": hideTitleOnInput
  });
  var tabIndex = disabled || readonly ? -1 : tabIndexProp;
  var uniqueId = useMemo(function () {
    return inputId || "textarea-" + getInstanceId();
  }, []);
  return /*#__PURE__*/React.createElement("div", _extends({
    className: fieldWrapperClass,
    style: style,
    ref: ref,
    onKeyUp: onkeydown
  }, otherProps), /*#__PURE__*/React.createElement("div", {
    className: fieldContainerClass
  }, /*#__PURE__*/React.createElement("div", {
    className: fieldTitleWrapperClass
  }, /*#__PURE__*/React.createElement("label", {
    className: "ap-field-title",
    htmlFor: "field-input-" + uniqueId,
    id: "field-input-label-" + uniqueId,
    "aria-owns": "field-input-" + uniqueId,
    "aria-disabled": disabled
  }, title)), /*#__PURE__*/React.createElement("div", {
    className: ClassNames("ap-field-wrapper", {
      "keyboard-focus": keyboardFocus,
      "focus": focus
    }),
    onClick: handleClick
  }, /*#__PURE__*/React.createElement("textarea", _extends({
    id: "field-input-" + uniqueId,
    className: "ap-field-input",
    ref: mergeRefs(textAreaRef, inputRef, inputRefProp),
    maxLength: maxLength,
    "aria-invalid": false,
    readOnly: readonly,
    "aria-readonly": readonly,
    disabled: disabled,
    "aria-disabled": disabled,
    required: required,
    "aria-required": required,
    value: value,
    name: name,
    onKeyDown: onKeyDown,
    onChange: handleChange,
    onBlur: handleInputBlur,
    onFocus: handleInputFocus,
    tabIndex: tabIndex
  }, inputProps)), maxLength && /*#__PURE__*/React.createElement("div", {
    className: "ap-field-counter"
  }, /*#__PURE__*/React.createElement("span", null, value.length, "/", maxLength))), /*#__PURE__*/React.createElement("div", {
    className: "ap-field-content"
  }, children, error && errorNode)));
});
export default TextArea;